import React from 'react';
import { keyframes, styled } from '@mui/material';
import { PieArcDatum } from 'd3';
import { DatumId, WithDataLabels } from '../../core/Chart.types';
import { computeRingArc, MIN_HIGHLIGHT_ANGLE, SELECTED_RADIUS_FACTOR, STROKE_WIDTH } from './PieSlice.utils';
import { PolylineLabel, polylineLabelClassNames } from './PolylineLabel';
import { TagLabel, tagLabelClassNames } from './TagLabel';

export const pieSliceClassNames = {
  arc: 'Chart-PieArc',
  ring: 'Chart-PieRing',
} as const;

const PIE_TRANSITION_DURATION = '220ms';
const PIE_TRANSITION_EASING = 'cubic-bezier(0.2, 0, 0, 1)';
const SURFACE_COLOR = 'var(--color-surface-base-primary)';

const PIE_LABEL_FADE_IN = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

export type SliceProps<Datum> = WithDataLabels & {
  arcPath: string;
  baseRadius: number;
  innerRadius: number;
  index: number;
  highlighted: boolean;
  selected: boolean;
  isInteractive: boolean;
  color: string;
  label?: string | number;
  datumId: DatumId;
  total?: number;
  datum: Datum;
  polylineScale?: number;
} & PieArcDatum<Datum> &
  React.ComponentProps<'g'>;

export const PieSlice = <Datum,>({
  startAngle,
  endAngle,
  padAngle,
  arcPath,
  highlighted,
  selected,
  isInteractive,
  dataLabelMode,
  index,
  color,
  label,
  datumId,
  baseRadius,
  innerRadius,
  total,
  datum,
  polylineScale = 1,
  onPointerEnter,
  onPointerLeave,
  onClick,
  ref,
  ...groupProps
}: SliceProps<Datum>) => {
  const pieArc = { startAngle, endAngle, padAngle };
  const { ringArc, ringOuterRadius, ringInnerRadius } = computeRingArc(pieArc, baseRadius, selected);
  const outerR = selected ? baseRadius * (1 + SELECTED_RADIUS_FACTOR) : baseRadius;
  const angularSpan = endAngle - startAngle;
  const showStroke = angularSpan >= MIN_HIGHLIGHT_ANGLE;
  const gradientId = !showStroke ? `pie-edge-grad-${index}` : undefined;
  const gradientStrokeOpacity = gradientId ? Math.min(1, angularSpan / MIN_HIGHLIGHT_ANGLE) : undefined;

  return (
    <SliceGroup
      role="listitem"
      data-selected={selected || undefined}
      data-highlighted={highlighted || undefined}
      data-interactive={isInteractive ? '' : undefined}
      data-index={index}
      data-datum-id={datumId}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
      ref={ref}
      {...groupProps}>
      {gradientId && <EdgeStrokeGradient id={gradientId} outerRadius={outerR} innerRadius={innerRadius} />}
      <path
        data-index={index}
        className={pieSliceClassNames.arc}
        d={arcPath}
        fill={color}
        stroke={showStroke ? SURFACE_COLOR : gradientId ? `url(#${gradientId})` : 'none'}
        strokeWidth={STROKE_WIDTH}
        strokeOpacity={gradientStrokeOpacity}
      />
      {ringArc && <path aria-hidden="true" className={pieSliceClassNames.ring} d={ringArc} fill={color} />}
      {dataLabelMode === 'polyline' && (
        <PolylineLabel
          datum={datum}
          angles={pieArc}
          ringInnerRadius={ringInnerRadius}
          ringOuterRadius={ringOuterRadius}
          highlighted={highlighted}
          selected={selected}
          color={color}
          total={total}
          scale={polylineScale}
        />
      )}
      {label && dataLabelMode === 'tag' && (
        <TagLabel
          label={label}
          angles={pieArc}
          ringInnerRadius={ringInnerRadius}
          ringOuterRadius={ringOuterRadius}
          highlighted={highlighted}
          selected={selected}
          color={color}
        />
      )}
    </SliceGroup>
  );
};

type EdgeStrokeGradientProps = { id: string; outerRadius: number; innerRadius: number };

const EdgeStrokeGradient = React.memo(({ id, outerRadius, innerRadius }: EdgeStrokeGradientProps) => {
  const isDonut = innerRadius > 0;
  const innerEdgeStart = innerRadius / outerRadius;
  const innerEdgeEnd = (innerRadius + STROKE_WIDTH) / outerRadius;
  const midpoint = (innerRadius + outerRadius) / 2 / outerRadius;
  const outerEdgeStart = (outerRadius - STROKE_WIDTH) / outerRadius;

  return (
    <radialGradient id={id} cx={0} cy={0} r={outerRadius} gradientUnits="userSpaceOnUse">
      {isDonut && <stop offset={innerEdgeStart} stopColor={SURFACE_COLOR} />}
      {isDonut ? (
        <stop offset={innerEdgeEnd} stopColor={SURFACE_COLOR} stopOpacity={0} />
      ) : (
        <>
          <stop offset={innerEdgeStart} stopColor={SURFACE_COLOR} />
          <stop offset={midpoint} stopColor={SURFACE_COLOR} stopOpacity={0} />
        </>
      )}
      <stop offset={outerEdgeStart} stopColor={SURFACE_COLOR} stopOpacity={0} />
      <stop offset={1} stopColor={SURFACE_COLOR} />
    </radialGradient>
  );
});

const SliceGroup = styled('g')(({ theme }) => ({
  [`& .${pieSliceClassNames.arc}`]: {
    cursor: 'default',
    transformBox: 'fill-box',
    transformOrigin: 'center',
    transition: theme.transitions.create(['opacity', 'filter', 'transform', 'd'], {
      duration: PIE_TRANSITION_DURATION,
      easing: PIE_TRANSITION_EASING,
    }),
  },
  [`&[data-interactive] .${pieSliceClassNames.arc}`]: {
    cursor: 'pointer',
  },
  [`&[data-selected] .${pieSliceClassNames.arc}`]: {
    transform: 'scale(1.015)',
  },
  [`&[data-highlighted] .${pieSliceClassNames.arc}`]: {
    filter: 'brightness(1.05)',
  },

  [`g[data-has-selection] &:not([data-selected]) .${pieSliceClassNames.arc}`]: {
    opacity: 0.15,
  },

  [`& .${pieSliceClassNames.ring}`]: {
    pointerEvents: 'none',
    opacity: 0,
    transition: theme.transitions.create(['opacity', 'd'], {
      duration: PIE_TRANSITION_DURATION,
      easing: PIE_TRANSITION_EASING,
    }),
  },
  [`&[data-highlighted] .${pieSliceClassNames.ring}`]: {
    opacity: 0.5,
  },
  [`g[data-has-selection] &[data-highlighted]:not([data-selected]) .${pieSliceClassNames.ring}`]: {
    opacity: 0.05,
  },

  [`& .${polylineLabelClassNames.line}, & .${polylineLabelClassNames.label}`]: {
    pointerEvents: 'none',
  },
  [`& .${polylineLabelClassNames.line}`]: {
    transition: theme.transitions.create(['opacity', 'd'], {
      duration: PIE_TRANSITION_DURATION,
      easing: PIE_TRANSITION_EASING,
    }),
    animation: `${PIE_LABEL_FADE_IN} ${PIE_TRANSITION_DURATION} ${PIE_TRANSITION_EASING}`,
  },
  [`& .${polylineLabelClassNames.label}`]: {
    userSelect: 'none',
    transition: theme.transitions.create(['opacity', 'x', 'y'], {
      duration: PIE_TRANSITION_DURATION,
      easing: PIE_TRANSITION_EASING,
    }),
    animation: `${PIE_LABEL_FADE_IN} ${PIE_TRANSITION_DURATION} ${PIE_TRANSITION_EASING}`,
  },

  [`& .${tagLabelClassNames.root}`]: {
    pointerEvents: 'none',
    transition: theme.transitions.create(['opacity', 'x', 'y'], {
      duration: PIE_TRANSITION_DURATION,
      easing: PIE_TRANSITION_EASING,
    }),
    animation: `${PIE_LABEL_FADE_IN} ${PIE_TRANSITION_DURATION} ${PIE_TRANSITION_EASING}`,
  },
  [`& .${tagLabelClassNames.content}`]: {
    transition: theme.transitions.create('transform', {
      duration: PIE_TRANSITION_DURATION,
      easing: PIE_TRANSITION_EASING,
    }),
  },

  [`g[data-has-selection] &:not([data-selected]) .${polylineLabelClassNames.line},
    g[data-has-selection] &:not([data-selected]) .${polylineLabelClassNames.label},
    g[data-has-selection] &:not([data-selected]) .${tagLabelClassNames.root}`]: {
    opacity: 0.25,
  },
}));
